import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  Legend,
} from 'recharts';
import { ibkrService, ChartResponse, RegimeZone, StrikeMarker, ChartBar } from '../../services/ibkrService';
import { ema, rsi, macd, bollinger, vwap } from '../../utils/indicators';
import type { Bar as IndicatorBar } from '../../utils/indicators';

// ── Recommendation type (passed from IBKRAnalysis) ─────────────────────────

export interface Leg {
  action: string;
  qty: number;
  strike: number;
  type: string;
  expiry: string;
  price: number;
}

export interface Recommendation {
  strategy: string;
  dte: number;
  legs: Leg[];
  max_profit: number;
  max_profit_net?: number;
  max_loss: number;
  max_loss_net?: number;
  // Break-Even-Punkte. Long-Optionen + Vertical Spreads liefern einen Wert,
  // Iron Condor / Straddle / Strangle zwei. Leeres Array, wenn unbekannt.
  break_even: number[];
  explanation: string;
}

// ── Props ──────────────────────────────────────────────────────────────────

interface IBKRSymbolChartProps {
  symbol: string;
  recommendation?: Recommendation;
}

// ── Constants ──────────────────────────────────────────────────────────────

const TIMEFRAMES = ['1D', '1W', '1M', '3M', '6M', '1Y'];

const REGIME_COLORS: Record<string, string> = {
  TREND_UP: 'rgba(76, 175, 80, 0.15)',
  TREND_DOWN: 'rgba(244, 67, 54, 0.15)',
  RANGE: 'rgba(33, 150, 243, 0.10)',
  VOLATILE: 'rgba(255, 152, 0, 0.15)',
};

// ── Helper: format unix timestamp for axis label ───────────────────────────

function formatTime(unix: number, timeframe: string): string {
  const d = new Date(unix * 1000);
  if (timeframe === '1D') {
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  if (timeframe === '1W' || timeframe === '1M') {
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  }
  return d.toLocaleDateString('de-DE', { month: '2-digit', year: '2-digit' });
}

// ── SubPanel: shared wrapper for Volume / RSI / MACD sub-charts ───────────

interface SubPanelProps {
  height: number;
  data: any[];
  yDomain?: [number | string, number | string];
  yFormatter?: (v: number) => string;
  children: React.ReactNode;
}

const SubPanel: React.FC<SubPanelProps> = ({ height, data, yDomain, yFormatter, children }) => (
  <Box sx={{ width: '100%', height, mt: 1 }}>
    <ResponsiveContainer>
      <ComposedChart data={data} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="t" hide />
        <YAxis domain={yDomain} tickFormatter={yFormatter} />
        <Tooltip
          labelFormatter={(t) => new Date((t as number) * 1000).toLocaleString('de-DE')}
        />
        {children}
      </ComposedChart>
    </ResponsiveContainer>
  </Box>
);

// ── Component ──────────────────────────────────────────────────────────────

const IBKRSymbolChart: React.FC<IBKRSymbolChartProps> = ({
  symbol,
  recommendation,
}) => {
  const [timeframe, setTimeframe] = useState('1D');
  const [overlays, setOverlays] = useState({
    ema20: true,
    ema50: false,
    vwap: true,
    bb: false,
    vol: true,
  });
  const [panels, setPanels] = useState({ rsi: false, macd: false });

  const [data, setData] = useState<ChartResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch chart data whenever symbol or timeframe changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    ibkrService
      .getChart(symbol, timeframe)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Fehler beim Laden des Charts');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, timeframe]);

  // ── Stable base arrays ────────────────────────────────────────────────

  const bars = useMemo(() => data?.bars ?? [], [data]);
  const closes = useMemo(() => bars.map((b: ChartBar) => b.c), [bars]);

  // ── Per-indicator memoized series ─────────────────────────────────────

  const ema20Series = useMemo(
    () => (overlays.ema20 ? ema(closes, 20) : []),
    [closes, overlays.ema20],
  );

  const ema50Series = useMemo(
    () => (overlays.ema50 ? ema(closes, 50) : []),
    [closes, overlays.ema50],
  );

  const vwapSeries = useMemo(
    () => (overlays.vwap ? vwap(bars as IndicatorBar[]) : []),
    [bars, overlays.vwap],
  );

  const bbSeries = useMemo(
    () => (overlays.bb ? bollinger(closes, 20, 2) : null),
    [closes, overlays.bb],
  );

  const rsiSeries = useMemo(
    () => (panels.rsi ? rsi(closes, 14) : []),
    [closes, panels.rsi],
  );

  const macdSeries = useMemo(
    () => (panels.macd ? macd(closes, 12, 26, 9) : null),
    [closes, panels.macd],
  );

  // ── Chart row objects (joined from individual series) ─────────────────

  const chartRows = useMemo(
    () =>
      bars.map((b: ChartBar, i: number) => ({
        t: b.t,
        close: b.c,
        vol: b.v,
        ema20:
          ema20Series[i] !== undefined && !isNaN(ema20Series[i])
            ? ema20Series[i]
            : undefined,
        ema50:
          ema50Series[i] !== undefined && !isNaN(ema50Series[i])
            ? ema50Series[i]
            : undefined,
        vwap:
          vwapSeries[i] !== undefined && !isNaN(vwapSeries[i])
            ? vwapSeries[i]
            : undefined,
        bbU:
          bbSeries && !isNaN(bbSeries.upper[i]) ? bbSeries.upper[i] : undefined,
        bbM:
          bbSeries && !isNaN(bbSeries.mid[i]) ? bbSeries.mid[i] : undefined,
        bbL:
          bbSeries && !isNaN(bbSeries.lower[i]) ? bbSeries.lower[i] : undefined,
        rsi:
          rsiSeries[i] !== undefined && !isNaN(rsiSeries[i])
            ? rsiSeries[i]
            : undefined,
        macd:
          macdSeries && !isNaN(macdSeries.macd[i])
            ? macdSeries.macd[i]
            : undefined,
        macdSignal:
          macdSeries && !isNaN(macdSeries.signal[i])
            ? macdSeries.signal[i]
            : undefined,
        macdHist:
          macdSeries && !isNaN(macdSeries.histogram[i])
            ? macdSeries.histogram[i]
            : undefined,
      })),
    [bars, ema20Series, ema50Series, vwapSeries, bbSeries, rsiSeries, macdSeries],
  );

  // ── Toggle helpers ─────────────────────────────────────────────────────

  const toggleOverlay = (key: keyof typeof overlays) =>
    setOverlays((prev) => ({ ...prev, [key]: !prev[key] }));

  const togglePanel = (key: keyof typeof panels) =>
    setPanels((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Regime zones & strike markers ────────────────────────────────────

  const regimeZones: RegimeZone[] = data?.regime_zones ?? [];

  const strikeMarkers = useMemo<StrikeMarker[]>(
    () => data?.strike_markers ?? [],
    [data],
  );

  const recommendationStrikes = useMemo<StrikeMarker[]>(() => {
    if (!recommendation) return [];
    return recommendation.legs.map((leg) => ({
      strike: leg.strike,
      type: leg.type === 'Call' ? 'C' : 'P',
      action: leg.action,
      expiry: leg.expiry,
    }));
  }, [recommendation]);

  const allStrikes = useMemo(
    () => [...strikeMarkers, ...recommendationStrikes],
    [strikeMarkers, recommendationStrikes],
  );

  // ── Axis tick formatter ───────────────────────────────────────────────

  const tickFormatter = (t: number) => formatTime(t, timeframe);

  // ── Panel visibility ──────────────────────────────────────────────────

  const showVolPanel = overlays.vol;
  const showRsiPanel = panels.rsi;
  const showMacdPanel = panels.macd;

  // Heights
  const priceHeight = 300;
  const subPanelHeight = 100;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {symbol} — Chart
        </Typography>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
          alignItems: 'center',
        }}
      >
        {/* Timeframe group */}
        <ButtonGroup size="small" variant="outlined" aria-label="Zeitrahmen">
          {TIMEFRAMES.map((tf) => (
            <Button
              key={tf}
              variant={timeframe === tf ? 'contained' : 'outlined'}
              onClick={() => setTimeframe(tf)}
              aria-label={`Zeitrahmen ${tf}`}
            >
              {tf}
            </Button>
          ))}
        </ButtonGroup>

        {/* Overlay toggles */}
        <ButtonGroup size="small" variant="outlined" aria-label="Overlays">
          {(
            [
              { key: 'ema20', label: 'EMA20' },
              { key: 'ema50', label: 'EMA50' },
              { key: 'vwap', label: 'VWAP' },
              { key: 'bb', label: 'BB' },
              { key: 'vol', label: 'Vol' },
            ] as { key: keyof typeof overlays; label: string }[]
          ).map(({ key, label }) => (
            <Button
              key={key}
              variant={overlays[key] ? 'contained' : 'outlined'}
              onClick={() => toggleOverlay(key)}
              aria-label={`Overlay ${label}`}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>

        {/* Sub-panel toggles */}
        <ButtonGroup size="small" variant="outlined" aria-label="Panels">
          {(
            [
              { key: 'rsi', label: 'RSI' },
              { key: 'macd', label: 'MACD' },
            ] as { key: keyof typeof panels; label: string }[]
          ).map(({ key, label }) => (
            <Button
              key={key}
              variant={panels[key] ? 'contained' : 'outlined'}
              onClick={() => togglePanel(key)}
              aria-label={`Panel ${label}`}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Charts */}
      {!loading && !error && chartRows.length > 0 && (
        <Box>
          {/* ── Price chart ────────────────────────────────────────────── */}
          <ResponsiveContainer width="100%" height={priceHeight}>
            <ComposedChart data={chartRows} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis
                dataKey="t"
                tickFormatter={tickFormatter}
                tick={{ fontSize: 11 }}
                minTickGap={40}
              />
              <YAxis
                domain={['auto', 'auto']}
                tick={{ fontSize: 11 }}
                width={60}
                tickFormatter={(v: number) => v.toFixed(0)}
              />
              <Tooltip
                labelFormatter={(t: number) => new Date(t * 1000).toLocaleString('de-DE')}
                formatter={(value: number, name: string) => [value.toFixed(2), name]}
              />
              <Legend />

              {/* Regime zones as translucent ReferenceArea bands */}
              {regimeZones.map((zone, i) => (
                <ReferenceArea
                  key={`zone-${i}`}
                  x1={zone.from}
                  x2={zone.to}
                  fill={REGIME_COLORS[zone.regime] ?? 'rgba(128,128,128,0.1)'}
                  label={{ value: zone.regime, fontSize: 9, fill: '#aaa' }}
                />
              ))}

              {/* Strike markers — color by action: BUY=green, SELL=red */}
              {allStrikes.map((sm, i) => (
                <ReferenceLine
                  key={`strike-${i}`}
                  y={sm.strike}
                  stroke={sm.action.toUpperCase() === 'BUY' ? '#4caf50' : '#f44336'}
                  strokeDasharray="4 2"
                  label={{
                    value: `${sm.action} ${sm.strike}${sm.type}`,
                    fontSize: 10,
                    fill: sm.action.toUpperCase() === 'BUY' ? '#4caf50' : '#f44336',
                    position: 'right',
                  }}
                />
              ))}

              {/*
               * Close-price line — Recharts has no native candlestick renderer.
               * We display the close price as a simple line chart instead.
               */}
              <Line
                type="monotone"
                dataKey="close"
                name="Kurs"
                stroke="#90caf9"
                dot={false}
                strokeWidth={1.5}
              />

              {overlays.ema20 && (
                <Line
                  type="monotone"
                  dataKey="ema20"
                  name="EMA20"
                  stroke="#ffa726"
                  dot={false}
                  strokeWidth={1}
                  connectNulls
                />
              )}

              {overlays.ema50 && (
                <Line
                  type="monotone"
                  dataKey="ema50"
                  name="EMA50"
                  stroke="#ab47bc"
                  dot={false}
                  strokeWidth={1}
                  connectNulls
                />
              )}

              {overlays.vwap && (
                <Line
                  type="monotone"
                  dataKey="vwap"
                  name="VWAP"
                  stroke="#26c6da"
                  dot={false}
                  strokeWidth={1}
                  strokeDasharray="5 3"
                  connectNulls
                />
              )}

              {overlays.bb && (
                <>
                  <Line
                    type="monotone"
                    dataKey="bbU"
                    name="BB Upper"
                    stroke="#ef5350"
                    dot={false}
                    strokeWidth={1}
                    strokeDasharray="2 2"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="bbM"
                    name="BB Mid"
                    stroke="#bdbdbd"
                    dot={false}
                    strokeWidth={1}
                    strokeDasharray="2 2"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="bbL"
                    name="BB Lower"
                    stroke="#42a5f5"
                    dot={false}
                    strokeWidth={1}
                    strokeDasharray="2 2"
                    connectNulls
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>

          {/* ── Volume panel ────────────────────────────────────────────── */}
          {showVolPanel && (
            <SubPanel
              height={subPanelHeight}
              data={chartRows}
              yFormatter={(v) =>
                v >= 1_000_000
                  ? `${(v / 1_000_000).toFixed(1)}M`
                  : `${(v / 1000).toFixed(0)}K`
              }
            >
              <Bar dataKey="vol" name="Volumen" fill="#546e7a" opacity={0.7} />
            </SubPanel>
          )}

          {/* ── RSI panel ───────────────────────────────────────────────── */}
          {showRsiPanel && (
            <>
              <Typography variant="caption" sx={{ pl: 1, color: 'text.secondary' }}>
                RSI (14)
              </Typography>
              <SubPanel
                height={subPanelHeight}
                data={chartRows}
                yDomain={[0, 100]}
                yFormatter={(v) => v.toFixed(0)}
              >
                <ReferenceLine
                  y={70}
                  stroke="#ef5350"
                  strokeDasharray="3 2"
                  label={{ value: '70', fontSize: 9, fill: '#ef5350' }}
                />
                <ReferenceLine
                  y={30}
                  stroke="#42a5f5"
                  strokeDasharray="3 2"
                  label={{ value: '30', fontSize: 9, fill: '#42a5f5' }}
                />
                <Line
                  type="monotone"
                  dataKey="rsi"
                  name="RSI"
                  stroke="#ce93d8"
                  dot={false}
                  strokeWidth={1.5}
                  connectNulls
                />
              </SubPanel>
            </>
          )}

          {/* ── MACD panel ──────────────────────────────────────────────── */}
          {showMacdPanel && (
            <>
              <Typography variant="caption" sx={{ pl: 1, color: 'text.secondary' }}>
                MACD (12, 26, 9)
              </Typography>
              <SubPanel
                height={subPanelHeight}
                data={chartRows}
                yFormatter={(v) => v.toFixed(2)}
              >
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.3)" />
                <Bar dataKey="macdHist" name="Hist" fill="#78909c" opacity={0.8} />
                <Line
                  type="monotone"
                  dataKey="macd"
                  name="MACD"
                  stroke="#29b6f6"
                  dot={false}
                  strokeWidth={1.5}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="macdSignal"
                  name="Signal"
                  stroke="#ff7043"
                  dot={false}
                  strokeWidth={1}
                  strokeDasharray="4 2"
                  connectNulls
                />
              </SubPanel>
            </>
          )}
        </Box>
      )}

      {/* Empty state */}
      {!loading && !error && chartRows.length === 0 && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            Keine Chart-Daten verfuegbar.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default IBKRSymbolChart;
